/**
 * LuviaRx — Admin COA Requests tab.
 *
 * Shows all incoming Certificate of Analysis requests from product pages.
 * Per row: customer name/email, product/variant, date, status, and action
 * buttons for uploading a PDF (per product slug) and sending the on-file PDF
 * to the customer as an email attachment.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileCheck2, Upload, Mail, RefreshCcw, CheckCircle2, Clock, Package, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  fulfilled: "bg-sage-100 text-sage-700 border-sage-200",
  closed:    "bg-charcoal-100 text-charcoal-500 border-charcoal-200",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function CoaRequestsTab({ token }) {
  const [rows, setRows] = useState([]);
  const [available, setAvailable] = useState({}); // slug -> {filename, size_bytes, uploaded_at}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // id currently being acted on
  const [flash, setFlash] = useState("");
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null); // { slug, productName }

  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data: reqs }, { data: files }] = await Promise.all([
        axios.get(`${API}/admin/coa-requests`, { headers: H }),
        axios.get(`${API}/admin/coa/available`, { headers: H }),
      ]);
      setRows(reqs);
      const map = {};
      files.forEach((f) => { map[f.product_slug] = f; });
      setAvailable(map);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load COA requests");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 3500); };

  const openUploadFor = (slug, productName) => {
    setUploadingFor({ slug, productName });
    // Reset the input so uploading the same filename twice re-triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    const ctx = uploadingFor;
    if (!file || !ctx) return;
    setBusy(`upload-${ctx.slug}`);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/admin/coa/${ctx.slug}/upload`, form, {
        headers: { ...H, "Content-Type": "multipart/form-data" },
      });
      showFlash(`✓ Uploaded ${file.name} for ${ctx.productName}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(null);
      setUploadingFor(null);
    }
  };

  const removeCoaFor = async (slug, productName) => {
    if (!window.confirm(`Delete the uploaded COA for ${productName}? The product page will revert to "Request COA".`)) return;
    setBusy(`delete-${slug}`);
    try {
      await axios.delete(`${API}/admin/coa/${slug}`, { headers: H });
      showFlash(`✓ Removed COA for ${productName}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  const sendToCustomer = async (row) => {
    if (!window.confirm(`Email the current COA for ${row.product_name} to ${row.email}?`)) return;
    setBusy(`send-${row._id}`);
    setError("");
    try {
      await axios.post(`${API}/admin/coa-requests/${row._id}/send`, {}, { headers: H });
      showFlash(`✓ COA sent to ${row.email}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Send failed");
    } finally { setBusy(null); }
  };

  const markClosed = async (row) => {
    setBusy(`close-${row._id}`);
    try {
      await axios.patch(`${API}/admin/coa-requests/${row._id}`, { status: "closed" }, { headers: H });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(null); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const rest = rows.filter((r) => r.status !== "pending");

  return (
    <div data-testid="admin-coa-tab">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onFilePicked}
        data-testid="coa-hidden-file-input"
      />

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal-600 mb-1 flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-[#8B2252]" strokeWidth={2} />
            COA Requests
          </h2>
          <p className="text-sm text-charcoal-400">
            {pending.length} pending · {available && Object.keys(available).length} product{Object.keys(available).length === 1 ? "" : "s"} with COA on file
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="coa-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border-2 border-charcoal-100 text-charcoal-500 hover:bg-cream-200/70 disabled:opacity-50">
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
          Refresh
        </button>
      </div>

      {flash && (
        <div data-testid="coa-flash" className="mb-4 p-3 rounded-2xl bg-sage-100 text-sage-700 text-sm">{flash}</div>
      )}
      {error && (
        <div data-testid="coa-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-charcoal-400 text-sm">Loading requests…</div>
      ) : rows.length === 0 ? (
        <div data-testid="coa-empty" className="surface-card p-10 text-center">
          <FileCheck2 className="w-10 h-10 mx-auto text-charcoal-300 mb-3" strokeWidth={1.5} />
          <p className="font-display text-xl text-charcoal-600 mb-1">No requests yet</p>
          <p className="text-sm text-charcoal-400">Customer COA requests from product pages will show up here.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <p className="overline mb-3">Pending</p>
              <div className="space-y-3 mb-8">
                {pending.map((r) => (
                  <CoaRow key={r._id} row={r} available={available[r.product_id]} busy={busy}
                    onUpload={openUploadFor} onSend={sendToCustomer} onClose={markClosed} onRemove={removeCoaFor} />
                ))}
              </div>
            </>
          )}
          {rest.length > 0 && (
            <>
              <p className="overline mb-3">Fulfilled &amp; closed</p>
              <div className="space-y-3">
                {rest.map((r) => (
                  <CoaRow key={r._id} row={r} available={available[r.product_id]} busy={busy}
                    onUpload={openUploadFor} onSend={sendToCustomer} onClose={markClosed} onRemove={removeCoaFor} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const CoaRow = ({ row, available, busy, onUpload, onSend, onClose, onRemove }) => {
  const isPending = row.status === "pending";
  const StatusIcon = isPending ? Clock : CheckCircle2;
  return (
    <article data-testid={`coa-row-${row._id}`} className="surface-card p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status] || STATUS_STYLES.pending}`}>
              <StatusIcon className="w-3 h-3" strokeWidth={2} />
              {row.status}
            </span>
            <span className="text-[11px] text-charcoal-400">{fmtDate(row.created_at)}</span>
            {available && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-sage-50 text-sage-700 border-sage-200">
                <Package className="w-3 h-3" strokeWidth={2} /> PDF on file
              </span>
            )}
          </div>
          <p className="font-display text-lg font-bold text-charcoal-600 truncate">
            {row.product_name}
            {row.variant_label && (
              <span className="text-charcoal-400 font-normal text-sm ml-2">· {row.variant_label}</span>
            )}
          </p>
          <p className="text-sm text-charcoal-500 mt-1">
            <span className="font-medium text-charcoal-600">{row.user_name || "(guest)"}</span>
            {" · "}
            <a href={`mailto:${row.email}`} className="text-[#8B2252] hover:underline">{row.email}</a>
          </p>
          {row.message && (
            <p className="text-sm text-charcoal-500 mt-2 bg-cream-200/50 rounded-xl px-3 py-2 whitespace-pre-wrap max-w-2xl">
              “{row.message}”
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 md:min-w-[220px] shrink-0">
          <button type="button" onClick={() => onUpload(row.product_id, row.product_name)}
            disabled={busy === `upload-${row.product_id}`}
            data-testid={`coa-upload-${row._id}`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-full border-2 border-charcoal-100 text-charcoal-600 hover:bg-cream-200/70 disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" strokeWidth={2} />
            {available ? "Replace PDF" : "Upload PDF"}
          </button>
          <button type="button" onClick={() => onSend(row)}
            disabled={!available || busy === `send-${row._id}` || row.status === "fulfilled"}
            data-testid={`coa-send-${row._id}`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-full bg-[#8B2252] text-white hover:bg-[#711b42] disabled:opacity-50 disabled:cursor-not-allowed">
            <Mail className="w-3.5 h-3.5" strokeWidth={2} />
            {row.status === "fulfilled" ? "Sent ✓" : busy === `send-${row._id}` ? "Sending…" : "Send to customer"}
          </button>
          {available && (
            <button type="button" onClick={() => onRemove(row.product_id, row.product_name)}
              disabled={busy === `delete-${row.product_id}`}
              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full">
              <Trash2 className="w-3 h-3" strokeWidth={2} />
              Remove uploaded PDF
            </button>
          )}
          {row.status === "pending" && (
            <button type="button" onClick={() => onClose(row)}
              disabled={busy === `close-${row._id}`}
              className="text-[11px] text-charcoal-400 hover:text-charcoal-600 hover:underline text-center">
              Mark as closed (skip email)
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
